// import mbxClient from "@mapbox/mapbox-sdk";
import { MapiResponse } from '@mapbox/mapbox-sdk/lib/classes/mapi-response';
import mbxGeocodingV6, { GeocodeResponse } from '@mapbox/mapbox-sdk/services/geocoding-v6'
import { NewProjectFormFields } from "containers/NewProject/NewProject";
import { FormikErrors } from "formik";
import { useAtom } from 'jotai';
import { useEffect, useMemo } from "react";
import { toast } from 'react-toastify';
import { Coords, debouncedAddressAtom, debouncedCoordsAtom, Location, locationSuggestionsAtom } from 'store/atoms/NewProject/newProjectAtoms';
import CONFIG from "utils/config";

type SetFieldValueFunc = (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<NewProjectFormFields>>;

export const useNewProjectLocationGeocoder = (
  values: NewProjectFormFields,
  setFieldValue: SetFieldValueFunc,
  callbacks?: {
    onFetchedSugestions: () => void
  }
) => {

  const [debouncedAddress, setDebouncedAddress] = useAtom(debouncedAddressAtom);
  const [debouncedCoords, setDebouncedCoords] = useAtom(debouncedCoordsAtom);
  const [suggestions, setSuggestions] = useAtom(locationSuggestionsAtom);

  const mbxClient = useMemo(() => {
    if (!CONFIG.MAPBOX_ACCESS_TOKEN) return;
    return mbxGeocodingV6({
      accessToken: CONFIG.MAPBOX_ACCESS_TOKEN,
    })
  }, []);

  // // just a change observer to observe state changes during development
  // useEffect(() => {
  //   console.debug("values changed: ", values);
  // }, [values])

  // // change observer to log suggestions
  // useEffect(() => {
  //   console.debug("suggestions changed: ", suggestions);
  // }, [suggestions])

  const fetchSuggestionsBasedOffAddress = (address: string) => {
    return mbxClient?.forwardGeocode({
      query: address,
    }).send();
  };

  const fetchSuggestionsBasedOffCoords = (coords: Coords) => {
    if (!coords) return;

    return mbxClient?.reverseGeocode({
      latitude: coords?.lat,
      longitude: coords?.lng,
    }).send();
  }

  // debounce user input
  useEffect(() => {
    if (!mbxClient) return;

    const timeout = setTimeout(async () => {
      setDebouncedAddress(values.location.address);
    }, CONFIG.MAPBOX_DEBOUNCE_TIME_INTERVAL);

    return () => clearTimeout(timeout);
  }, [values.location.address]);

  // debounce coords
  // useEffect(() => {
  //   if (!mbxClient) return;

  //   const timeout = setTimeout(async () => {
  //     setDebouncedCoords(values.location.coords);
  //   }, CONFIG.MAPBOX_DEBOUNCE_TIME_INTERVAL)

  //   return () => clearTimeout(timeout);
  // }, [values.location.coords?.lat, values.location.coords?.lng])

  useEffect(() => {

    const coords = values.location.coords;

    if (!coords?.lat || !coords.lng) return;

    // coords changed due to suggestion change
    if (suggestions.some((suggestion) => suggestion.coords?.lat === coords.lat && suggestion.coords.lng === coords.lng)) {
      return;
    }

    setFieldValue('location', {
      ...values.location,
      address: `Lat: ${coords?.lat}, Lon: ${coords?.lng}`
    } as Location)
  }, [values.location.coords])

  // when coords change -> do nothing (no need to update debounced coords which in turn updates suggestions)

  const populateNewSuggestions = async (options: {
    basedOff: "address",
    address: string,
  } | {
    basedOff: "coords",
    coords: Coords
  }) => {
    if (
      (options.basedOff === "address" && options.address === "")
      || (options.basedOff === "coords" && !options.coords)
    ) {
      setSuggestions([]);
      return;
    }

    const newSuggestionsRes: MapiResponse<GeocodeResponse> | undefined =
      options.basedOff === "address"
        ? await fetchSuggestionsBasedOffAddress(debouncedAddress)
        : await fetchSuggestionsBasedOffCoords(values.location.coords);

    if (!newSuggestionsRes) {
      toast.error("Failed to fetch location details");
      return;
    }
    const newSuggestions = newSuggestionsRes.body;

    const formattedNewSuggestions: Location[] = newSuggestions.features.map((f) => ({
      id: f.id,
      // @ts-ignore
      address: f.properties.full_address, // full_address is not being detected by typescript
      coords: {
        lat: f.properties.coordinates.latitude,
        lng: f.properties.coordinates.longitude,
      }
    }))

    setSuggestions(formattedNewSuggestions);

    return formattedNewSuggestions;
  }

  // when debounced address changes, fetch suggestions
  useEffect(() => {

    if (suggestions.find((sugg) => sugg.id === values.location.id)) {
      // selected from dropdown suggestions
      return;
    }

    populateNewSuggestions({
      basedOff: "address",
      address: debouncedAddress,
    });

  }, [debouncedAddress])

  useEffect(() => {

    if (suggestions.find((sugg) => sugg.id === values.location.id)) {
      // selected from dropdown suggestions
      return;
    }

    populateNewSuggestions({
      basedOff: "coords",
      coords: debouncedCoords
    }).then(suggestions => {
      if (!suggestions) return;
      // also set it to the first suggestion
      if (callbacks?.onFetchedSugestions) {
        callbacks.onFetchedSugestions();
      }
    })

  }, [debouncedCoords?.lat, debouncedCoords?.lng])
};
